import { useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Upload, CheckCircle, Loader2, Camera, ImageIcon } from 'lucide-react'

interface ImageUploadWidgetProps {
  label: string
  imagePreview: string | null
  isUploading: boolean
  isUploaded: boolean
  onFileSelect: (file: File) => void
  maxSizeKB: number
  error?: string
}

export function ImageUploadWidget({
  label,
  imagePreview,
  isUploading,
  isUploaded,
  onFileSelect,
  error,
}: ImageUploadWidgetProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      onFileSelect(file)
    }
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="space-y-3">
      <Label className="flex items-center gap-2 text-sm font-medium text-slate-700">
        {label === 'Your Photo' ? (
          <Camera className="w-4 h-4 text-slate-400" />
        ) : (
          <ImageIcon className="w-4 h-4 text-slate-400" />
        )}
        {label}
        <span className="text-destructive">*</span>
      </Label>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {imagePreview ? (
        <div className="space-y-3">
          {/* Image Preview Card */}
          <div className="relative rounded-xl overflow-hidden border-2 border-slate-200 bg-slate-50">
            <img
              src={imagePreview}
              alt={label}
              className="w-full h-48 sm:h-56 object-contain"
            />

            {/* Status Badge */}
            {isUploaded && (
              <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1.5 rounded-full flex items-center gap-1.5 text-sm font-medium shadow-lg">
                <CheckCircle className="w-4 h-4" />
                Uploaded
              </div>
            )}

            {isUploading && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="bg-white rounded-xl px-4 py-3 flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <span className="font-medium text-slate-900">Uploading...</span>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <Button
            type="button"
            variant="outline"
            onClick={handleClick}
            disabled={isUploading}
            className="w-full h-11"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Camera className="w-4 h-4 mr-2" />
                Change Image
              </>
            )}
          </Button>
        </div>
      ) : (
        /* Upload Button */
        <button
          type="button"
          onClick={handleClick}
          disabled={isUploading}
          className={`
            w-full p-8 rounded-xl border-2 border-dashed transition-all duration-200
            ${isUploading
              ? 'border-primary bg-primary/5 cursor-wait'
              : 'border-slate-300 hover:border-primary/50 hover:bg-slate-50'
            }
          `}
        >
          <div className="flex flex-col items-center gap-3">
            {isUploading ? (
              <>
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
                <p className="font-medium text-slate-900">Uploading...</p>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                  <Upload className="w-6 h-6 text-slate-400" />
                </div>
                <p className="font-medium text-slate-900">
                  Click to upload
                </p>
              </>
            )}
          </div>
        </button>
      )}

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  )
}
